import type { InitializeUploadData, StreamUploadResult, UploadProgress, DocumentQueryParams, DownloadUrlResponse, Document } from '../../../shared/types';
import api from '../axios';
 
// Documents Service Class
export class DocumentService {
  // Document Upload Operations
  async initializeUpload(data: InitializeUploadData): Promise<Document> {
    const response = await api.post<Document>('/documents/initialize', data);
    return response.data;
  }

  async uploadStream(documentId: string, file: File, enableCompression = true): Promise<StreamUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('enableCompression', enableCompression.toString());

    const response = await api.post<StreamUploadResult>(
      `/documents/upload-stream/${documentId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  async uploadMultiple(
    files: File[],
    tenantId: string,
    brandId: string,
    userId: string
  ): Promise<StreamUploadResult[]> {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('tenantId', tenantId);
    formData.append('brandId', brandId);
    formData.append('userId', userId);

    const response = await api.post<StreamUploadResult[]>('/documents/upload-multiple', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Upload Management
  async getUploadProgress(documentId: string): Promise<UploadProgress | null> {
    const response = await api.get<UploadProgress>(`/documents/${documentId}/progress`);
    return response.data;
  }

  async cancelUpload(documentId: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(`/documents/${documentId}/cancel`);
    return response.data;
  }

  async resumeUpload(documentId: string, fromByte = 0): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(`/documents/${documentId}/resume`, {
      fromByte
    });
    return response.data;
  }

  // Document Retrieval
  async getDocuments(params?: DocumentQueryParams): Promise<Document[]> {
    const queryParams = new URLSearchParams();
    
    if (params?.skip) queryParams.append('skip', params.skip.toString());
    if (params?.take) queryParams.append('take', params.take.toString());
    if (params?.tenantId) queryParams.append('tenantId', params.tenantId);
    if (params?.brandId) queryParams.append('brandId', params.brandId);
    if (params?.status) queryParams.append('status', params.status);

    const url = queryParams.toString() 
      ? `/documents?${queryParams.toString()}` 
      : '/documents';
    const response = await api.get<Document[]>(url);
    return response.data;
  }

  async getDocumentById(documentId: string): Promise<Document | null> {
    const response = await api.get<Document>(`/documents/${documentId}`);
    return response.data;
  }

  async getDocumentsByTenant(tenantId: string): Promise<Document[]> {
    const response = await api.get<Document[]>(`/documents/tenant/${tenantId}`);
    return response.data;
  }

  async getDocumentsByBrand(brandId: string): Promise<Document[]> {
    const response = await api.get<Document[]>(`/documents/brand/${brandId}`);
    return response.data;
  }

  // Document Download
  async downloadDocument(documentId: string, userId?: string): Promise<Blob> {
    const queryParams = new URLSearchParams();
    if (userId) queryParams.append('userId', userId);

    const url = queryParams.toString() 
      ? `/documents/${documentId}/download?${queryParams.toString()}` 
      : `/documents/${documentId}/download`;
    const response = await api.get(url, {
      responseType: 'blob',
    });
    return response.data;
  }

  async getDownloadUrl(
    documentId: string,
    userId?: string,
    expiresIn?: number
  ): Promise<DownloadUrlResponse> {
    const queryParams = new URLSearchParams();
    if (userId) queryParams.append('userId', userId);
    if (expiresIn) queryParams.append('expiresIn', expiresIn.toString());

    const url = queryParams.toString() 
      ? `/documents/${documentId}/download-url?${queryParams.toString()}` 
      : `/documents/${documentId}/download-url`;
    const response = await api.get<DownloadUrlResponse>(url);
    return response.data;
  }

  // Document Management
  async updateDocument(
    documentId: string,
    data: { name?: string; priority?: number; metadata?: any }
  ): Promise<Document> {
    const response = await api.put<Document>(`/documents/${documentId}`, data);
    return response.data;
  }

  async deleteDocument(documentId: string): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>(`/documents/${documentId}`);
    return response.data;
  }

  // Access Control
  async grantAccess(documentId: string, userId: string): Promise<{ success: boolean }> {
    const response = await api.post<{ success: boolean }>(`/documents/${documentId}/access`, {
      userId
    });
    return response.data;
  }

  async revokeAccess(documentId: string, userId: string): Promise<{ success: boolean }> {
    const response = await api.delete<{ success: boolean }>(`/documents/${documentId}/access/${userId}`);
    return response.data;
  }

  // Utility Methods
  async uploadSingleFile(
    file: File,
    tenantId: string,
    brandId: string,
    userId: string,
    options?: {
      enableCompression?: boolean;
      priority?: number;
      metadata?: Record<string, any>;
    }
  ): Promise<StreamUploadResult> {
    // Initialize upload
    
    const document = await this.initializeUpload({
      userId,
      tenantId,
      brandId,
      fileName: file.name,
      mimeType: file.type,
      fileSize: file.size,
      enableCompression: options?.enableCompression,
      priority: options?.priority,
      metadata: options?.metadata,
    });

    // Upload file
    return this.uploadStream(document.id, file, options?.enableCompression);
  }

}

// Create and export singleton instance
export const documentService = new DocumentService();

// Export individual methods for convenience
export const {
  initializeUpload,
  uploadStream,
  uploadMultiple,
  getUploadProgress,
  cancelUpload,
  resumeUpload,
  getDocuments,
  getDocumentById,
  getDocumentsByTenant,
  getDocumentsByBrand,
  downloadDocument,
  getDownloadUrl,
  updateDocument,
  deleteDocument,
  grantAccess,
  revokeAccess,
  uploadSingleFile
} = documentService;